import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Building2, Users, Clock, Wallet, ShieldCheck, LogOut, UserPlus,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "employee"] },
  { to: "/app/users", label: "Employers", icon: UserPlus, roles: ["admin"] },
  { to: "/app/companies", label: "Companies", icon: Building2, roles: ["admin", "manager"] },
  { to: "/app/employees", label: "Employees", icon: Users, roles: ["admin", "manager"] },
  { to: "/app/time", label: "Time Tracker", icon: Clock, roles: ["admin", "manager"] },
  { to: "/app/payroll", label: "Payroll", icon: Wallet, roles: ["admin", "manager"] },
  { to: "/app/approvals", label: "Verification Queue", icon: ShieldCheck, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Back-End Verifier", manager: "HR / Payroll Manager", employee: "Employee" };

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-300 flex flex-col fixed h-screen">
        <div className="px-5 py-5 border-b border-slate-800">
          <Logo light />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active
                    ? "bg-sky-500 text-white shadow-lg shadow-sky-500/20"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-sky-500/20 text-sky-300 flex items-center justify-center font-bold uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate" data-testid="current-user-name">{user?.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{ROLE_LABEL[user?.role]}</p>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">{children}</div>
      </main>
    </div>
  );
}
